/**
 * @fileoverview RiddleLayoutInverted - Formato 2 (Invertido):
 * Opciones de respuesta verticales a la izquierda (55% ancho) e Imagen / Silueta a la derecha (45% ancho).
 * Aceleración por GPU (Layer Compositing), alta visibilidad en 1080p y 4K.
 * 
 * @module components/Screen/Sections/Riddle/RiddleLayoutInverted
 */

import React from 'react';

/**
 * Resuelve la URL absoluta de un recurso multimedia.
 * @param {string} url - URL relativa o absoluta.
 * @returns {string} URL resuelta.
 */
const resolveMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) {
        return url;
    }
    const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
    return `${apiBase}${url.startsWith('/') ? url.substring(1) : url}`;
};

const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * Componente Layout Invertido para la Pantalla Gigante de Adivina Adivinador.
 * 
 * @param {Object} props
 * @param {Object} props.riddle - Datos de la adivinanza (image, options, response, instruction, etc.).
 * @param {boolean} props.isItemVisible - Indica si la silueta ya fue revelada en color.
 * @param {boolean} props.showCorrectAnswer - Indica si se debe resaltar la opción correcta en pantalla.
 * @param {string} [props.fontFamily] - Familia tipográfica institucional.
 * @param {string} [props.textColor] - Color principal de los textos.
 * @returns {React.ReactElement}
 */
export const RiddleLayoutInverted = ({
    riddle,
    isItemVisible = false,
    showCorrectAnswer = true,
    fontFamily = '"GFS Didot", serif',
    textColor = '#FFFFFF'
}) => {
    const options = Array.isArray(riddle?.options) ? riddle.options : [];
    const silhouetteImage = resolveMediaUrl(riddle?.image);
    const revealedImage = resolveMediaUrl(riddle?.response?.image) || silhouetteImage;
    const responseText = riddle?.response?.text || '';

    // Determinar si la respuesta coincide para resaltar
    const isOptionCorrect = (option) => {
        if (option?.isCorrect === true) return true;
        if (responseText && option?.text && option.text.trim().toLowerCase() === responseText.trim().toLowerCase()) {
            return true;
        }
        return false;
    };

    return (
        <section className="RiddleLayout RiddleLayout--Inverted">
            {/* Columna Izquierda: Opciones Verticales (55% ancho) */}
            <div className="RiddleLayout__OptionsColumn">
                <ul className="RiddleLayout__OptionsList">
                    {options.map((option, index) => {
                        const correct = isOptionCorrect(option);
                        const shouldHighlight = isItemVisible && showCorrectAnswer && correct;
                        const label = OPTION_LABELS[index] || `${index + 1}`;

                        return (
                            <li
                                key={option.id ?? index}
                                className={`RiddleLayout__OptionItem ${shouldHighlight ? 'RiddleLayout__OptionItem--Correct' : ''} ${isItemVisible && !correct && showCorrectAnswer ? 'RiddleLayout__OptionItem--Dimmed' : ''}`}
                            >
                                <span className="RiddleLayout__OptionItem__Badge">
                                    {label}
                                </span>

                                <span
                                    className="RiddleLayout__OptionItem__Text"
                                    style={{ fontFamily, color: textColor }}
                                >
                                    {option.text}
                                </span>

                                {shouldHighlight && (
                                    <span className="RiddleLayout__OptionItem__Check">
                                        <i className="bi bi-check-circle-fill" />
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>

            {/* Columna Derecha: Imagen / Silueta (45% ancho) */}
            <div className="RiddleLayout__MediaColumn">
                <div className={`RiddleLayout__MediaCard ${isItemVisible ? 'RiddleLayout__MediaCard--Revealed' : 'RiddleLayout__MediaCard--Silhouette'}`}>
                    <div className="RiddleLayout__ImageWrapper">
                        <img
                            src={isItemVisible ? revealedImage : silhouetteImage}
                            alt={isItemVisible ? 'Personaje Revelado' : 'Silueta Misteriosa'}
                            className={`RiddleLayout__Image ${isItemVisible ? 'RiddleLayout__Image--Color' : 'RiddleLayout__Image--Silhouette'}`}
                        />
                    </div>

                    {/* Cartel de personaje revelado */}
                    {isItemVisible && responseText && (
                        <div className="RiddleLayout__RevealedBadge">
                            <i className="bi bi-stars RiddleLayout__RevealedBadge__Icon" />
                            <span className="RiddleLayout__RevealedBadge__Text" style={{ fontFamily }}>
                                {responseText}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default RiddleLayoutInverted;
